import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {Headline, P} from 'UI/components/layout/Text';

/**
 * The props for the `SectionHeader` component.
 * Configures a section title with optional right-aligned action text.
 */
interface ComponentProps {
    /** Optional action label displayed on the right (e.g. `"View All"`). */
    actionLabel?: string;
    /** Callback fired when the action label is clicked. */
    onAction?(): void;
    /**
     * The `testId` property represents a unique identifier assigned
     * to a component for testing purposes. It is applied as `data-cy`
     * attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'SectionHeader'
     */
    testId?: string;
    /** The section title text. */
    title: string;
}

/**
 * A section header row with a large title and optional right-aligned action link.
 * Used to introduce content sections such as "Featured in Augsburg" + "View All".
 *
 * @param props             The component props.
 * @param props.actionLabel Optional right-side action text.
 * @param props.onAction    Callback when the action is pressed.
 * @param props.testId      Test ID for automation selectors. Defaults to `'SectionHeader'`.
 * @param props.title       The section title.
 * @returns A JSX element rendering the SectionHeader.
 *
 * @example
 * ```tsx
 * <SectionHeader title="Featured in Augsburg" actionLabel="View All" onAction={handleViewAll} />
 * <SectionHeader title="All Tours" />
 * ```
 */
const SectionHeader = ({actionLabel, onAction, testId = 'SectionHeader', title}: ComponentProps) => (
    <Row data-cy={testId}>
        <Title $size="headline" as="h2" data-cy={`${testId}-title`}>{title}</Title>
        {actionLabel && (
            <ActionButton
                data-cy={`${testId}-action`}
                type="button"
                onClick={onAction}
            >
                <ActionLabel $size="labelMedium" as="span">{actionLabel}</ActionLabel>
            </ActionButton>
        )}
    </Row>
);

SectionHeader.displayName = 'SectionHeader';

export {SectionHeader};

// ── Styled ──────────────────────────────────────────────────

const Row = styled.div`
    align-items: flex-end;
    display: flex;
    justify-content: space-between;
    padding: 0 ${spacing(1)};
`;

const Title = styled(Headline)`
    color: ${({theme}) => theme.colors.primaryDefault};
`;

const ActionButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    margin: 0;
    padding: 0;
`;

const ActionLabel = styled(P)`
    color: ${({theme}) => theme.colors.accentDefault};
    font-weight: 700;
`;